import type { IntlShape, MessageDescriptor } from 'react-intl';

import { patientContextApprovalMessages as m } from './patient-context-approval.messages';

export const UPDATE_PATIENT_CONTEXT_TOOL_NAME = 'update_patient_context';
export const CREATE_PATIENT_PROFILE_TOOL_NAME = 'create_patient_profile';

/** Profile fields the cards show, in the order they are listed. */
const FIELD_KEYS = [
  'first_name',
  'last_name',
  'birth_date',
  'gender',
  'phone',
  'email',
  'allergies',
  'current_medications',
  'past_medical_history',
  'additional_context',
  'demographic_string',
] as const;

export type PatientContextFieldKey = (typeof FIELD_KEYS)[number];

export type PatientContextCurrent = Partial<
  Record<PatientContextFieldKey, string | null>
>;

export interface PatientContextApprovalDiff {
  key: PatientContextFieldKey;
  label: string;
  before: string;
  after: string;
}

export interface PatientContextApprovalView {
  title: string;
  subtitle: string;
  diffs: PatientContextApprovalDiff[];
}

const GENDER_LABELS: Record<string, MessageDescriptor> = {
  male: m.genderMale,
  female: m.genderFemale,
  other: m.genderOther,
  unknown: m.genderUnknown,
};

const readId = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

/**
 * Profile id from the tool input, then from a patient-scoped project.
 * The update tool often omits the id and lets the server use the project target.
 */
export const readPatientProfileId = (
  input: Record<string, unknown> | undefined,
  dataScope?: { kind?: string | null; patient_profile_id?: string | null } | null
): string | undefined => {
  const fromInput = readId(input?.patient_profile_id) ?? readId(input?.patient_id);
  if (fromInput) return fromInput;
  if (dataScope?.kind === 'patient') return readId(dataScope.patient_profile_id);
  return undefined;
};

/**
 * Matches the patient list: the identifier (`demographic_string`) wins, then a
 * first and last name, then an em dash.
 */
const patientLabel = (profile: PatientContextCurrent): string => {
  const identifier = profile.demographic_string?.trim();
  if (identifier) return identifier;
  const full = [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim();
  return full || '—';
};

const readProposed = (
  input: Record<string, unknown> | undefined,
  key: PatientContextFieldKey
): string | null | undefined => {
  if (!input || !Object.prototype.hasOwnProperty.call(input, key)) return undefined;
  const value = input[key];
  if (value === null) return null;
  if (typeof value !== 'string') return undefined;
  return value;
};

const normalize = (value: string | null | undefined): string => value?.trim() ?? '';

const formatIsoDate = (intl: IntlShape, value: string): string | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return undefined;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return undefined;
  }
  return intl.formatDate(date, { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatValue = (
  intl: IntlShape,
  key: PatientContextFieldKey,
  raw: string | null | undefined
): string => {
  const trimmed = normalize(raw);
  if (!trimmed) return intl.formatMessage(m.none);
  if (key === 'birth_date') {
    const formatted = formatIsoDate(intl, trimmed);
    if (formatted) return formatted;
  }
  if (key === 'gender') {
    const gender = GENDER_LABELS[trimmed];
    if (gender) return intl.formatMessage(gender);
  }
  return trimmed;
};

const joinPhrases = (
  intl: IntlShape,
  phrases: string[],
  messages: {
    one: MessageDescriptor;
    two: MessageDescriptor;
    many: MessageDescriptor;
  }
): string => {
  const first = phrases[0] ?? '';
  const second = phrases[1] ?? '';
  if (phrases.length === 1) {
    return intl.formatMessage(messages.one, { field: first });
  }
  if (phrases.length === 2) {
    return intl.formatMessage(messages.two, { first, second });
  }
  return intl.formatMessage(messages.many, {
    earlier: phrases.slice(0, -1).join(', '),
    last: phrases[phrases.length - 1] ?? '',
  });
};

const proposedFields = (input: Record<string, unknown> | undefined) =>
  FIELD_KEYS.flatMap((key) => {
    const value = readProposed(input, key);
    return value === undefined ? [] : [{ key, value }];
  });

const fieldPhrases = (intl: IntlShape, fields: { key: PatientContextFieldKey }[]) =>
  fields.map(({ key }) => intl.formatMessage(m.fieldPhrase, { field: key }));

const toDiff = (
  intl: IntlShape,
  key: PatientContextFieldKey,
  before: string | null | undefined,
  after: string | null
): PatientContextApprovalDiff => ({
  key,
  label: intl.formatMessage(m.fieldLabel, { field: key }),
  before: formatValue(intl, key, before),
  after: formatValue(intl, key, after),
});

/**
 * Reads an `update_patient_context` payload into the card's title, subtitle and
 * diffs. Returns null when nothing recognizable would change, so the caller can
 * fall back to the generic approval card.
 *
 * `current` is null until the profile loads. The subtitle still names every
 * proposed field, but there are no diffs, so Before is never shown as None for
 * a profile that has not arrived.
 */
export const readPatientContextApproval = (
  intl: IntlShape,
  input: Record<string, unknown> | undefined,
  current: PatientContextCurrent | null
): PatientContextApprovalView | null => {
  const proposed = proposedFields(input);
  const changed = current
    ? proposed.filter(({ key, value }) => normalize(value) !== normalize(current[key]))
    : proposed;
  if (changed.length === 0) return null;

  return {
    title: current
      ? intl.formatMessage(m.titleForPatient, { patient: patientLabel(current) })
      : intl.formatMessage(m.titleWithoutPatient),
    subtitle: joinPhrases(intl, fieldPhrases(intl, changed), {
      one: m.subtitleOne,
      two: m.subtitleTwo,
      many: m.subtitleMany,
    }),
    diffs: current
      ? changed.map(({ key, value }) => toDiff(intl, key, current[key], value))
      : [],
  };
};

/**
 * Reads a `create_patient_profile` payload. The new patient is the tool input.
 * Returns null when no recognizable field is set, so the caller can fall back
 * to the generic approval card.
 */
export const readCreatePatientProfileApproval = (
  intl: IntlShape,
  input: Record<string, unknown> | undefined
): PatientContextApprovalView | null => {
  const proposed = proposedFields(input);
  if (proposed.length === 0) return null;

  const patient = patientLabel({
    first_name: readProposed(input, 'first_name'),
    last_name: readProposed(input, 'last_name'),
    demographic_string: readProposed(input, 'demographic_string'),
  });

  return {
    title: intl.formatMessage(m.createTitleForPatient, { patient }),
    subtitle: joinPhrases(intl, fieldPhrases(intl, proposed), {
      one: m.createSubtitleOne,
      two: m.createSubtitleTwo,
      many: m.createSubtitleMany,
    }),
    diffs: proposed.map(({ key, value }) => toDiff(intl, key, null, value)),
  };
};
